import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { ContainerClient } from '@azure/storage-blob'
import { List, message, Modal, Tooltip, Upload } from 'antd'
import type { UploadProps } from 'antd'
import { InboxOutlined } from '@ant-design/icons'
import styled from 'styled-components'

import { colours, sizes } from '@qflow/theme'

import { ColourButton, icons, Spacer, Spinner } from 'portal/lib/primitives'
import { trackMixpanel } from 'portal/store/mixpanel/actions'

import { useProjectDocumentsTokenLoader } from './useProjectDocumentsTokenLoader'

export const Files = ({ project }: FilesProps) => {
  const [allDocuments, setAllDocuments] = useState<BlobFile[]>([])
  const [reloadTrigger, setReloadTrigger] = useState<number>(0)

  const { loading, error } = useProjectDocumentsTokenLoader(project.projectId)
  const dispatch = useDispatch()
  const { accountId } = useParams<{
    accountId: string
  }>()

  const projectDocumentsToken = useSelector(
    (state: any) => state.projectDocumentsToken
  )

  useEffect(() => {
    if (
      typeof projectDocumentsToken.data === 'string' &&
      projectDocumentsToken.data !== ''
    ) {
      const containerClient: ContainerClient = new ContainerClient(
        projectDocumentsToken.data
      )

      async function fetchFileList() {
        const blobList: BlobFile[] = []
        const blobs = containerClient.listBlobsFlat()
        for await (const blob of blobs) {
          blobList.push(blob as BlobFile)
        }
        // Sort blobs alphabetically by name
        blobList.sort((a, b) => a.name.localeCompare(b.name))
        // Set the sorted list to state
        setAllDocuments(blobList)
      }

      fetchFileList()
    }
  }, [projectDocumentsToken.data, reloadTrigger])

  const fileUpload = useCallback(
    async options => {
      const { file, onSuccess, onError } = options
      try {
        const containerClient: ContainerClient = new ContainerClient(
          projectDocumentsToken.data
        )
        const blockBlobClient = containerClient.getBlockBlobClient(file.name)

        await blockBlobClient.upload(file, file.size, {
          blobHTTPHeaders: { blobCacheControl: 'max-age=3600' }
        })
        onSuccess(file)
      } catch (error) {
        console.error('File upload failed:', error)
        onError(error)
      }
    },
    [projectDocumentsToken.data]
  )

  const fileDelete = useCallback(
    async (fileName: string) => {
      try {
        const containerClient: ContainerClient = new ContainerClient(
          projectDocumentsToken.data
        )
        const blockBlobClient = containerClient.getBlockBlobClient(fileName)

        await blockBlobClient.delete()
        message.success(`${fileName} file deleted successfully.`)
      } catch (error) {
        console.error('File deletion failed:', error)
        message.error(`${fileName} file deletion failed.`)
      }
    },
    [projectDocumentsToken.data]
  )

  const fileDownload = useCallback(
    async (fileName: string) => {
      try {
        const containerClient: ContainerClient = new ContainerClient(
          projectDocumentsToken.data
        )
        const blockBlobClient = containerClient.getBlockBlobClient(fileName)

        const downloadBlockBlobResponse = await blockBlobClient.download(0)
        const blob = await downloadBlockBlobResponse.blobBody

        // Create a link to download the file
        if (!blob) {
          throw new Error('Failed to download the file: Blob is undefined')
        }
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      } catch (error) {
        console.error('File download failed:', error)
      }
    },
    [projectDocumentsToken.data]
  )

  const { Dragger } = Upload

  let uploadModal

  const types = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingm',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'application/x-zip',
    'application/x-zip-compressed',
    'application/zip',
    'application/zip-compressed',
    'text/csv',
    'image/jpeg',
    'image/png'
  ]

  const draggerProps: UploadProps = {
    name: 'file',
    accept: '.pdf,.doc,.docx,.xls,.xlsx,.cvs,.jpg,.png,.csv,.zip',
    multiple: true,
    customRequest: fileUpload,
    beforeUpload: file => {
      // Check if the file type is allowed
      if (!types.includes(file.type)) {
        console.error('Rejected file type:', file.type)
        message.error(
          'You can only upload PDF, DOC, XLS, CSV, JPG, PNG and ZIP files.'
        )
        return Upload.LIST_IGNORE // Prevent the file from being added to the list
      }
      return true // Allow the file to be uploaded
    },
    onChange(info) {
      const { status } = info.file
      if (status === 'uploading') {
        // Disable the Done button until upload is complete
        uploadModal.update(prevConfig => ({
          ...prevConfig,
          okButtonProps: {
            disabled: true
          }
        }))
      }
      if (status === 'done') {
        message.success(`${info.file.name} file uploaded successfully.`)
        // Re-enable the Done button if all files are uploaded
        if (info.fileList.every(file => file.status === 'done')) {
          uploadModal.update(prevConfig => ({
            ...prevConfig,
            okButtonProps: {
              disabled: false
            }
          }))
        }

        dispatch(
          trackMixpanel('frontend_files_upload_click', {
            account_id: accountId,
            project_id: project.projectId,
            file_name: info.file.name
          })
        )
      } else if (status === 'error') {
        message.error(`${info.file.name} file upload failed.`)
        // Re-enable the Done button if all files are uploaded
        if (info.fileList.every(file => file.status === 'done')) {
          uploadModal.update(prevConfig => ({
            ...prevConfig,
            okButtonProps: {
              disabled: false
            }
          }))
        }
      }
    }
  }

  const handleAddFile = () => {
    uploadModal = Modal.confirm({
      title: 'Upload files',
      closable: true,
      icon: null,
      width: 600,
      wrapClassName: 'upload-modal',
      okText: 'Done',
      okButtonProps: {
        disabled: false
      },
      cancelButtonProps: {
        style: {
          display: 'none' // Hide the cancel button
        }
      },
      content: (
        <ContentWrapper>
          <Dragger {...draggerProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ color: colours.TEAL }} />
            </p>
            <p className="ant-upload-text">
              Click or drag and drop your files here
            </p>
          </Dragger>
          <ExtraInfo>
            Supported formats: PDF, DOC, XLS, CSV, JPG, PNG, ZIP.
          </ExtraInfo>
        </ContentWrapper>
      ),
      afterClose: () => {
        // Trigger a reload to fetch the new file list
        setReloadTrigger(prev => prev + 1)
      }
    })
  }

  const formatSize = (size?: number): string => {
    if (!size) return '0 B'
    const units = ['B', 'KB', 'MB', 'GB', 'TB']
    let index = 0
    let formattedSize = size
    while (formattedSize >= 1024 && index < units.length - 1) {
      formattedSize /= 1024
      index++
    }
    return `${formattedSize.toFixed(0)} ${units[index]}`
  }

  if (error) {
    return <span>An error has occurred. Please try refreshing the page</span>
  }

  if (loading || !projectDocumentsToken.data) {
    return <Spinner />
  }

  return (
    <ProjectsData>
      <TitleBar>
        <h3>{project.name}</h3>
        <RightSide>
          <ColourButton.Trigger style={{ margin: '0' }} onClick={handleAddFile}>
            <icons.Upload size={sizes.SMALL} />
            <Spacer.Fine />
            Upload files
          </ColourButton.Trigger>
        </RightSide>
      </TitleBar>

      <Section>
        <h4>File Repository</h4>
        {/* Files set up */}
        <List
          size="small"
          bordered
          dataSource={allDocuments}
          locale={{
            emptyText: 'No documents uploaded yet.'
          }}
          style={{
            marginTop: '0.75rem',
            backgroundColor: colours.WHITE
          }}
          renderItem={(doc: BlobFile) => (
            <List.Item style={{ padding: '0' }}>
              <DataWrapper>
                <FileName
                  onClick={async () => {
                    await fileDownload(doc.name)
                    dispatch(
                      trackMixpanel('frontend_files_download_click', {
                        account_id: accountId,
                        project_id: project.projectId,
                        file_name: doc.name
                      })
                    )
                  }}
                >
                  {doc.name}
                </FileName>
                <RightSide>
                  <LastModified>
                    {doc.properties.lastModified?.toLocaleDateString()}
                  </LastModified>
                  <FileSize>
                    {formatSize(doc.properties?.contentLength)}
                  </FileSize>
                  <Tooltip title="Remove document">
                    <ColourButton.BasicBorderless
                      name={`remove-supplier-${doc.name.replace(/\s+/g, '-').toLowerCase()}`}
                      aria-label={'Remove document'}
                      style={{
                        padding: '0.25rem 9px',
                        borderRadius: '50%',
                        color: colours.RED_DARKMODE
                      }}
                      onClick={async () => {
                        await fileDelete(doc.name)
                        dispatch(
                          trackMixpanel('frontend_files_delete_click', {
                            account_id: accountId,
                            project_id: project.projectId,
                            file_name: doc.name
                          })
                        )
                        // Trigger a reload to fetch the new file list
                        setReloadTrigger(prev => prev + 1)
                      }}
                    >
                      <icons.TrashCan colour="currentColor" />
                    </ColourButton.BasicBorderless>
                  </Tooltip>
                </RightSide>
              </DataWrapper>
            </List.Item>
          )}
        />
      </Section>
    </ProjectsData>
  )
}

type FilesProps = {
  project: ProjectSettings
}

export interface ProjectSettings {
  name: string
  projectId: string
  maxAlertRules: number
  isOneClickLcaEnabled: boolean
  isEC3Enabled: boolean
}

type BlobFile = {
  name: string
  properties: {
    contentLength?: number
    lastModified?: Date
    etag?: string
  }
}

const ProjectsData = styled.div`
  flex-grow: 1;
  background-color: ${colours.WHITE};
  color: ${colours.CORE_NAVY};
  border-radius: 0.625rem;
  padding: 1rem 1.5rem 1rem 1.5rem;
  box-shadow: 0px 1px 3px 0px #00000026;
`
const TitleBar = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.875rem;
  min-height: 2rem;

  & > h3 {
    color: ${colours.CORE_NAVY};
    font-family: "Jakarta Sans", "Inter", sans-serif !important;
    font-size: 1.125rem;
    font-weight: 700;
    margin: 0;
`
const Section = styled.div`
  background-color: ${colours.CONTROL_PANEL_GREY};
  border-radius: 0.625rem;
  padding: 1rem 1.5rem 1.5rem 1.5rem;
  margin-bottom: 0.625rem;

  & > h4 {
    color: ${colours.CORE_NAVY};
    font-family: 'Jakarta Sans', 'Inter', sans-serif !important;
    font-size: 0.9375rem;
    font-weight: 700;
    margin-bottom: 0.25rem;
  }
`
const ContentWrapper = styled.div`
  margin: 0.5rem 0;
`
const FileName = styled.div`
  font-weight: 500;
  cursor: pointer;
  flex-grow: 1;

  &:hover {
    color: ${colours.SUCCESS};
  }
`
const RightSide = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
`
const LastModified = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${colours.MID_GREY};
`
const FileSize = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${colours.MID_GREY};
  min-width: 4rem;
  text-align: right;
`
const DataWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.25rem 0.25rem 0.25rem 0.625rem;
`
const ExtraInfo = styled.div`
  font-size: 0.75rem;
  color: ${colours.BATTLESHIP_GREY};
  margin: 0.5rem 0 0.5rem 0;
`
